import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';


const MusicSection = styled.div`
  width: 100%;
  max-width: 500px;
  margin: 10px auto 10px; /* Reduced top margin from 30px to 10px */
  text-align: center;
`;

const MusicTitle = styled.h2`
  color: #947060;
  font-family: 'Cormorant Garamond', serif;
  font-size: 1.8rem;
  margin-bottom: 15px;
  font-weight: 500;
`;

const MusicActionLink = styled.a`
  display: flex;
  align-items: center;
  text-decoration: none;
  color: #5a4a42;
  padding: 12px 20px;
  background-color: rgba(255, 255, 255, 0.9);
  border-radius: 30px;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(179, 139, 109, 0.2);
  font-family: 'Cormorant Garamond', serif;
  font-size: 1.1rem;
  letter-spacing: 0.5px;
  border: 1px solid rgba(179, 139, 109, 0.2);
  margin: 0 auto;
  max-width: fit-content;
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 6px 16px rgba(179, 139, 109, 0.3);
    background-color: white;
  }
`;

const MusicIcon = styled.img`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  margin-right: 12px;
  object-fit: cover;
`;

const MusicCaption = styled.p`
  font-family: 'Lato', sans-serif;
  color: #555;
  font-size: 1.1rem;
  line-height: 1.6;
  margin-bottom: 20px;
`;

const SpotifyContainer = styled.div`
  width: 100%;
  max-width: 450px;
  margin: 0 auto;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 6px 15px rgba(0, 0, 0, 0.1);
`;
const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  max-width: 800px;
  margin: 0 auto;
  padding: 0 20px 60px;
  overflow-x: hidden;
  box-sizing: border-box;
  
  @media (max-width: 768px) {
    padding: 0 15px 40px;
  }
  
  @media (max-width: 480px) {
    padding: 0 5px 30px;
  }
`;

const PageTitle = styled.h1`
  text-align: center;
  color: #947060;
  font-family: 'Cormorant Garamond', serif;
  font-size: 2.8rem;
  letter-spacing: 5px;
  margin: 20px 0; /* Reduced from 40px to 20px */
  position: relative;
  padding: 25px 0;
  font-weight: 400;
  text-transform: uppercase;
  
  @media (max-width: 768px) {
    font-size: 2.2rem;
    letter-spacing: 3px;
    margin: 15px 0; /* Reduced from 30px to 15px */
    padding: 20px 0;
  }
  
  @media (max-width: 480px) {
    font-size: 1.8rem;
    letter-spacing: 2px;
    margin: 10px 0; /* Reduced from 20px to 10px */
    padding: 15px 0;
  }
  
  &::before, &::after {
    content: "";
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
    height: 1px;
    background: linear-gradient(to right, transparent, #d4b59e, transparent);
    width: 280px;
    
    @media (max-width: 768px) {
      width: 80%;
      max-width: 280px;
    }
    
    @media (max-width: 480px) {
      width: 90%;
      max-width: 240px;
    }
  }
  
  &::before {
    top: 0;
  }
  
  &::after {
    bottom: 0;
  }
`;

const NameSpan = styled.span`
  display: block;
  font-family: 'Great Vibes', cursive;
  font-size: 2.4rem;
  letter-spacing: 1px;
  font-style: normal;
  text-transform: none;
  margin-top: 15px;
  color: #b38b6d;
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
  
  @media (max-width: 480px) {
    font-size: 1.8rem;
    margin-top: 8px;
  }
`;

const Timeline = styled.div`
  position: relative;
  width: 100%;
  max-width: 700px;
  margin: 10px 0; /* Reduced top margin from 50px to 20px */
  box-sizing: border-box;
  
  @media (max-width: 768px) {
    margin: 15px 0; /* Reduced from 30px to 15px */
    max-width: 100%;
  }
  
  @media (max-width: 480px) {
    padding: 0;
  }
  
  &::before {
    content: '';
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
    width: 2px;
    height: calc(100% - 100px);
    background: linear-gradient(to bottom, transparent 5%, #d4b59e 15%, #d4b59e 85%, transparent 95%);
    top: 50px;
    
    @media (max-width: 768px) {
      left: 20px;
    }
    
    @media (max-width: 480px) {
      left: 15px;
      width: 1.5px;
    }
  }
`;

const TimelineEvent = styled(motion.div)`
  display: flex;
  justify-content: ${props => props.align === 'right' ? 'flex-start' : 'flex-end'};
  width: 100%;
  margin: 80px 0;
  position: relative;
  box-sizing: border-box;
  
  @media (max-width: 768px) {
    justify-content: flex-start;
    padding-left: 60px;
    margin: 60px 0;
  }
  
  @media (max-width: 480px) {
    padding-left: 40px;
    padding-right: 0;
    margin: 40px 0;
    width: 100%;
  }
`;

const TimelineMarker = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background-color: #fcfaf7;
  border: 3px solid #d4b59e;
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2;
  box-shadow: 0 3px 6px rgba(0, 0, 0, 0.1);
  
  @media (max-width: 768px) {
    left: 30px;
    width: 50px;
    height: 50px;
  }
  
  @media (max-width: 480px) {
    left: 20px;
    width: 36px;
    height: 36px;
    border: 2px solid #d4b59e;
  }
`;

const MarkerDate = styled.div`
  color: #947060;
  font-family: 'Lato', sans-serif;
  font-size: 0.9rem;
  font-weight: 700;
  text-align: center;
  
  @media (max-width: 480px) {
    font-size: 0.75rem;
  }
`;

const EventContent = styled.div`
  width: 45%;
  background-color: #fcfaf7;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  overflow: hidden;
  
  @media (max-width: 768px) {
    width: calc(100% - 15px);
  }
  
  @media (max-width: 480px) {
    border-radius: 6px;
    width: calc(100% - 10px);
    box-shadow: 0 3px 8px rgba(0, 0, 0, 0.05);
  }
`;
const MemoryCard = styled(motion.div)`
  width: 85%;
  max-width: 600px;
  background-color: #fcfaf7;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  overflow: hidden;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  display: flex; // Add this to create side-by-side layout
  flex-direction: row; // Add this for horizontal layout
  
  &:hover {
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.12);
    transform: translateY(-5px);
  }
  
  @media (max-width: 768px) {
    width: 95%;
  }
  
  @media (max-width: 580px) {
    flex-direction: column; // Stack on smaller screens
    width: 98%;
    border-radius: 6px;
    box-shadow: 0 3px 8px rgba(0, 0, 0, 0.05);
  }
`;

const EventImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover; // This preserves aspect ratio while filling container
  transition: transform 0.5s ease;
  
  ${MemoryCard}:hover & {
    transform: scale(1.05);
  }
  
  @media (max-width: 768px) {
    height: 200px;
  }
  
  @media (max-width: 576px) {
    height: 170px;
  }
  
  @media (max-width: 480px) {
    height: 150px;
  }
`;

const EventDetails = styled.div`
  padding: 20px;
  flex-grow: 1; // Allow it to take up remaining space
  display: flex;
  flex-direction: column;
  justify-content: center;
  
  @media (max-width: 768px) {
    padding: 16px;
  }
  
  @media (max-width: 480px) {
    padding: 12px;
  }
`;

const EventTitle = styled.h3`
  color: #947060;
  font-family: 'Cormorant Garamond', serif;
  font-size: 1.5rem;
  margin-bottom: 10px;
  font-weight: 500;
  
  @media (max-width: 768px) {
    font-size: 1.4rem;
  }
  
  @media (max-width: 480px) {
    font-size: 1.3rem;
    margin-bottom: 8px;
  }
`;

const EventDescription = styled.p`
  font-family: 'Lato', sans-serif;
  color: #555;
  font-size: 1rem;
  line-height: 1.6;
  
  @media (max-width: 480px) {
    font-size: 0.95rem;
    line-height: 1.5;
  }
`;

const VideoContainer = styled(motion.div)`
  width: 100%;
  max-width: 700px;
  margin: 30px auto 0;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(179, 139, 109, 0.2);
  position: relative;
  padding-top: 56.25%; /* 16:9 Aspect Ratio */
  box-sizing: border-box;
  
  @media (max-width: 768px) {
    max-width: 100%;
  }
  
  @media (max-width: 480px) {
    border-radius: 6px;
    margin: 20px auto 0;
    width: calc(100% - 10px);
  }
`;

const StyledVideo = styled.video`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const FinalSection = styled.div`
  text-align: center;
  margin: 50px 0;
  
  @media (max-width: 768px) {
    margin: 40px 0;
  }
  
  @media (max-width: 480px) {
    margin: 30px 0;
  }
`;

const HeartIcon = styled.div`
  font-size: 2rem;
  color: #d4b59e;
  margin: 20px 0;
  
  @media (max-width: 480px) {
    font-size: 1.8rem;
    margin: 15px 0;
  }
`;

const Divider = styled.div`
  width: 100%;
  height: 2px;
  background: linear-gradient(to right, transparent, #d4b59e, transparent);
  margin: 40px 0;
  
  @media (max-width: 768px) {
    margin: 30px 0;
  }
  
  @media (max-width: 480px) {
    margin: 25px 0;
    height: 1.5px;
  }
`;

const VideoTitle = styled.h2`
  text-align: center;
  color: #947060;
  font-family: 'Cormorant Garamond', serif;
  font-size: 2rem;
  margin: 20px 0;
  font-weight: 500;
  
  @media (max-width: 768px) {
    font-size: 1.8rem;
  }
  
  @media (max-width: 480px) {
    font-size: 1.6rem;
    margin: 15px 0;
  }
`;

const VideoDescription = styled.p`
  text-align: center;
  font-family: 'Lato', sans-serif;
  color: #555;
  font-size: 1.1rem;
  line-height: 1.7;
  max-width: 700px;
  margin: 0 auto 40px;
  
  @media (max-width: 768px) {
    font-size: 1.05rem;
    margin: 0 auto 30px;
  }
  
  @media (max-width: 480px) {
    font-size: 1rem;
    line-height: 1.5;
    margin: 0 auto 25px;
    width: 95%;
  }
`;

const YearSection = styled(motion.div)`
  width: 100%;
  margin: 80px 0;
  position: relative;
  
  @media (max-width: 768px) {
    margin: 60px 0;
  }
  
  @media (max-width: 480px) {
    margin: 50px 0;
  }
`;

const YearMarker = styled.div`
  display: flex;
  flex-direction: column;
  align-items: ${props => props.align === "right" ? "flex-start" : "flex-end"};
  margin-bottom: 30px;
  padding: 0 20px;
  
  @media (max-width: 768px) {
    align-items: flex-start;
    margin-bottom: 20px;
    padding: 0 10px;
  }
`;

const YearBadge = styled.div`
  background: linear-gradient(135deg, #947060, #d4b59e);
  color: white;
  padding: 8px 25px;
  border-radius: 30px;
  font-family: 'Lato', sans-serif;
  font-weight: 700;
  font-size: 1.4rem;
  box-shadow: 0 4px 10px rgba(148, 112, 96, 0.3);
  
  @media (max-width: 768px) {
    font-size: 1.3rem;
    padding: 7px 20px;
  }
  
  @media (max-width: 480px) {
    font-size: 1.2rem;
    padding: 6px 18px;
  }
`;

const YearTitle = styled.h2`
  color: #947060;
  font-family: 'Cormorant Garamond', serif;
  font-size: 2rem;
  font-weight: 500;
  margin: 15px 0 5px;
  
  @media (max-width: 768px) {
    font-size: 1.8rem;
    margin: 12px 0 3px;
  }
  
  @media (max-width: 480px) {
    font-size: 1.6rem;
    margin: 10px 0 2px;
  }
`;

const EventsContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: ${props => props.align === "right" ? "flex-start" : "flex-end"};
  gap: 25px;
  
  @media (max-width: 768px) {
    align-items: center;
    gap: 20px;
    width: 100%;
  }
`;



const ImageContainer = styled.div`
  width: 40%; // Take up 40% of the card width
  min-height: 250px;
  overflow: hidden;
  flex-shrink: 0; // Prevent shrinking
  
  @media (max-width: 768px) {
    min-height: 200px;
  }
  
  @media (max-width: 580px) {
    width: 100%; // Full width on mobile
    height: 250px;
  }
  
  @media (max-width: 480px) {
    height: 220px;
  }
`;

const OurStory = () => {
  const timelineEvents = [
    {
      year: "2022",
      title: "How We Met",
      events: [
        {
          title: "First Match on Hinge",
          description: "Our story began when we matched on Hinge. Erika's prompt about pretending to be part of a Korean tour group in Spain caught Andrew's attention, despite Erika's extensive Dateline knowledge.",
          image: "/img/hinge.png"
        },
        {
          title: "First Date",
          description: "The story of our first date varies depending on who you ask - sometimes it was Korean BBQ, other times we had noodles. Regardless, one date led to another and the rest is history.",
          image: "/img/firstdate.png"
        },
        {
          title: "Halloween Together",
          description: "Our first Halloween was filled with pumpkin carving and haunted houses. The season may have been scary, but we weren't afraid to fall for each other!",
          image: "/img/pumpkin.jpg"
        },
        {
          title: "Afternoon Tea",
          description: "We discovered our shared love of culinary adventures at this elegant afternoon tea. Our relationship was already brewing into something special, one cup at a time.",
          image: "/img/afternoontea.jpg"
        }
      ],
      align: "right"
    },
    {
      year: "2023",
      title: "Our First Full Year",
      events: [
        {
          title: "SeaWorld Adventures",
          description: "Making a splash at SeaWorld! We discovered that whether we're watching dolphins leap or getting soaked in the splash zone, we're always having a whale of a good time together.",
          image: "/img/seaworld.jpg"
        },
        {
          title: "Broadway Magic",
          description: "Our first theater date watching Aladdin was a whole new world of fun! We discovered that whether we're exploring Agrabah or just enjoying a night out, the magic happens when we're together.",
          image: "/img/playbill.jpg"
        },
        {
          title: "Wedding Season",
          description: "Attending a wedding together gave us a glimpse into our own future. As we danced the night away and caught up with friends, we couldn't help but imagine what our own special day might look like someday.",
          image: "/img/wedding.jpg"
        },
        {
          title: "Golf Adventures",
          description: "Turns out Erika is a natural on the green! While Andrew was busy counting strokes, Erika was sinking putts like a pro. Who knew that our relationship would include golf lessons... with Andrew as the student!",
          image: "/img/golf.jpg"
        }
      ],
      align: "left"
    },
    {
      year: "2024",
      title: "Growing Together",
      events: [
        {
          title: "Snowboarding Adventure",
          description: "We hit the slopes together and discovered that love can survive even the coldest temperatures! While Erika showed off her impressive snowboarding skills, Andrew spent most of the day getting acquainted with the snow... face first.",
          image: "/img/snowboarding.jpg"
        },
        {
          title: "Rollercoaster Adventures",
          description: "Our relationship has had its ups and downs - especially at Knott's! Life may be a rollercoaster, but with each other to hold onto, we're enjoying every twist and turn of the ride.",
          image: "/img/rollercoaster.jpg"
        },
        {
          title: "Paint Night Date",
          description: "They say a picture is worth a thousand words, but our masterpieces from paint night mostly said 'we're having fun together!' Creating art side by side showed us that with the right person, even imperfect brushstrokes make beautiful memories.",
          image: "/img/paintnight.jpg"
        },
        {
          title: "Vegas Weekend",
          description: "What happens in Vegas... ends up in our wedding website! Our weekend getaway to Sin City was filled with bright lights, amazing shows, and proving that we don't need any luck when we have each other. Though Andrew and Erika might need some luck at the roulette table next time!",
          image: "/img/vegas.jpg"
        },
        {
          title: "Cruise Adventure",
          description: "Setting sail on our first cruise together was an adventure we'll never forget! From watching sunsets over the endless ocean to trying every dessert on the menu (twice), we discovered that our love definitely isn't seasick. Though both of us might have been a little...",
          image: "/img/cruise.jpg"
        }
      ],
      align: "right"
    },
    {
      year: "2025",
      title: "The Next Chapter",
      events: [
        {
          title: "The Proposal",
          description: "Andrew planned the perfect surprise proposal at our favorite spot overlooking the ocean. With the sunset in the background, he got down on one knee and asked the question that would change our lives forever.",
          image: "/img/proposal.jpg",
          hasVideo: true
        }
      ],
      align: "left"
    }
  ];

  return (
    <PageContainer>
      <PageTitle>
        Our Story
        <NameSpan>Erika & Andrew</NameSpan>
      </PageTitle>

      <MusicSection>
        <MusicTitle>Our Special Song</MusicTitle>
        <MusicCaption>The soundtrack to our love story</MusicCaption>
        <MusicActionLink 
          href="https://open.spotify.com/track/4JMC5leGU5JecMTybOuCpF#_=_" 
          target="_blank"
        >
          <MusicIcon src="/img/spotify_logo.jpg" alt="Spotify" />
          <span>Listen to Our Proposal Song</span>
        </MusicActionLink>
      </MusicSection>
      
      <Timeline>
        {timelineEvents.map((yearSection, yearIndex) => (
          <YearSection 
            key={yearIndex}
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.5 }}
          >
            <YearMarker align={yearSection.align}>
              <YearBadge>{yearSection.year}</YearBadge>
              <YearTitle>{yearSection.title}</YearTitle>
            </YearMarker>
            
            <EventsContainer align={yearSection.align}>
              {yearSection.events.map((event, eventIndex) => (
                <MemoryCard 
                  key={eventIndex}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.3, delay: eventIndex * 0.1 + 0.2 }}
                >
                <ImageContainer>
                  <EventImage src={event.image} alt={event.title} loading="lazy" />
                </ImageContainer>
                <EventDetails>
                  <EventTitle>{event.title}</EventTitle>
                  <EventDescription>{event.description}</EventDescription>
                </EventDetails>
              </MemoryCard>
              ))}
            </EventsContainer>
          </YearSection>
        ))}
      </Timeline>
      
      <Divider />
      
      {/* Grand Finale - The Proposal Video */}
      <VideoTitle>The Proposal</VideoTitle>
      <VideoDescription>
        And Erika said "Yes!" to forever.
      </VideoDescription>
      
      <VideoContainer
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-100px" }}
        transition={{ duration: 0.8 }}
      >
        <StyledVideo controls poster="/img/proposal_thumbnail.jpg">
          <source src="/video/proposal_video.mp4" type="video/mp4" />
          Your browser does not support the video tag.
        </StyledVideo>
      </VideoContainer>
      
      <FinalSection>
        <EventTitle>And now we're getting married!</EventTitle>
        <HeartIcon>❤️</HeartIcon>
        <EventDescription>
          We can't wait to celebrate our special day with all of our loved ones.
        </EventDescription>
      </FinalSection>
    </PageContainer>
  );
};

export default OurStory;